<template>
  <SectionCard title="Annual balance">
    <div class="d-flex flex-wrap gap-4">
      <div class="flex-grow-1 cg-balance-main">
        <div class="d-flex justify-content-between align-items-baseline mb-1">
          <p class="cg-micro mb-0">Incoming · certificates for the year</p>
          <p class="mb-0 cg-tabular small">
            <strong>{{ fmt(balance.supplyTotal) }}</strong> MWh
          </p>
        </div>
        <StackedBar :segments="scaled(balance.supplySegs)" class="mb-2" />
        <div class="d-flex flex-wrap gap-3 mb-4 small">
          <span
            v-for="s in balance.supplySegs"
            :key="s.key"
            class="d-inline-flex align-items-center gap-2"
          >
            <span class="cg-dot" :style="{ background: FILL[s.fill] }" />
            <span class="text-body-secondary">{{ s.label }}</span>
            <span class="cg-tabular fw-semibold">{{ fmt(s.value) }}</span>
          </span>
        </div>

        <div class="d-flex justify-content-between align-items-baseline mb-1">
          <p class="cg-micro mb-0">Outgoing · consumption to cover</p>
          <p class="mb-0 cg-tabular small">
            <strong>{{ fmt(balance.demandTotal) }}</strong> MWh
          </p>
        </div>
        <StackedBar :segments="scaled(balance.demandSegs)" class="mb-2" />
        <div class="d-flex flex-wrap gap-3 small">
          <span
            v-for="s in balance.demandSegs"
            :key="s.key"
            class="d-inline-flex align-items-center gap-2"
          >
            <span class="cg-dot" :style="{ background: FILL[s.fill] }" />
            <span class="text-body-secondary">{{ s.label }}</span>
            <span class="cg-tabular fw-semibold">{{ fmt(s.value) }}</span>
          </span>
        </div>
      </div>

      <div class="cg-balance-net">
        <p class="cg-micro mb-1">Net position</p>
        <StatBlock
          :value="signed(balance.net)"
          size="xl"
          :warn="balance.net < 0"
        />
        <p class="mb-0 text-body-secondary small">MWh across the group</p>
      </div>
    </div>
  </SectionCard>

  <div class="d-flex flex-wrap gap-3 mb-4">
    <div
      class="card flex-grow-1 cg-clickable cg-landing"
      @click="$emit('open-incoming')"
    >
      <div class="card-body">
        <h3 class="cg-card-title mb-1">Where supply comes from</h3>
        <p class="mb-3 text-body-secondary small">{{ rollup.supplyLine }}</p>
        <button type="button" class="btn btn-sm btn-outline-secondary w-100">
          Incoming · supply
        </button>
      </div>
    </div>
    <div
      class="card flex-grow-1 cg-clickable cg-landing"
      @click="$emit('open-outgoing')"
    >
      <div class="card-body">
        <h3 class="cg-card-title mb-1">Where it has to land</h3>
        <p class="mb-3 text-body-secondary small">{{ landingLine }}</p>
        <button type="button" class="btn btn-sm btn-outline-secondary w-100">
          Outgoing · allocation
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  BalanceSegment,
  GroupRollup,
} from "@/compliance_groups/fixture/derive";
import { FILL } from "@/compliance_groups/fixture/fills";
import { fmt, signed } from "@/compliance_groups/fixture/format";
import { computed } from "vue";
import SectionCard from "./SectionCard.vue";
import StackedBar from "./StackedBar.vue";
import StatBlock from "./StatBlock.vue";

const props = defineProps<{ rollup: GroupRollup }>();

const balance = computed(() => props.rollup.balance);
const landingLine = computed(() => {
  const domains = props.rollup.domainCount;
  const companies = props.rollup.companyCount;
  return `${domains} ${domains === 1 ? "domain" : "domains"} · ${companies} ${companies === 1 ? "company" : "companies"} · each with its own cancellation rule`;
});
defineEmits<{ "open-incoming": []; "open-outgoing": [] }>();

// Both bars share one scale so the difference between supply and demand is the visible thing.
const scaled = (segments: BalanceSegment[]) => {
  const scale =
    Math.max(balance.value.supplyTotal, balance.value.demandTotal) || 1;
  return segments.map((s) => ({
    fill: s.fill,
    pct: `${((s.value / scale) * 100).toFixed(2)}%`,
  }));
};
</script>

<style scoped lang="scss">
.cg-balance-main {
  flex: 1 1 520px;
  min-width: 0;
}
.cg-balance-net {
  flex: 0 1 210px;
}
.cg-landing {
  flex: 1 1 300px;
}
</style>
